import EmailIcon from '@mui/icons-material/Email';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useState } from 'react';
import { Alert, Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { ErrorBoundary } from '@sentry/react';
import { HOST_API_URL } from '../../../../config-global';
import { useTabs } from '../../../../context/TabContext';
import { useSelector } from '../../../../redux/store';
import { CustomAvatar } from '../../../../reusable-components/custom-avatar';
import FileThumbnail from '../../../../reusable-components/file-thumbnail';
import Markdown from '../../../../reusable-components/markdown';
import { stringToColor } from '../../../../utils/cssStyles';
import { convertToUnix, fMonthDayTimeYear } from '../../../../utils/formatTime';
import { replaceLinks, cleanMailTo } from '../../../../reusable-components/markdown/markdown-cleanup';

// ----------------------------------------------------------------------

TicketMessageItem.propTypes = {
  message: PropTypes.object,
  onOpenLightbox: PropTypes.func,
  index: PropTypes.number,
  fileArray: PropTypes.array,
  nextMessage: PropTypes.object,
};

export default function TicketMessageItem({
  message,
  onOpenLightbox,
  index,
  fileArray,
  nextMessage,
}) {

  const { addNewTabAndNavigate } = useTabs();

  const { file, fullName, id, note, sentOn, sentBy, signature } = message;

  const { email } = useSelector((state) => state.user.user);

  const [showAsPainText, setShowAsPainText] = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  const currentUser = sentBy === email;
  // const isImage = contentType === 'image';

  const name = fullName || sentBy;
  const nextMsgName = nextMessage?.fullName || nextMessage?.sentBy;

  const removeDetails =
    (nextMessage && nextMsgName === name && convertToUnix(nextMessage?.sentOn) - convertToUnix(sentOn) < 60000) ||
    false;

  const getTicketFile = async (noteId) => {
    window.open(`${HOST_API_URL}Api/TicketFile?noteId=${noteId}`, '_blank');
  };

  const downloadEmail = async () => {
    window.open(`${HOST_API_URL}Api/DownloadTicketMessageEmail?id=${message.id}`, '_blank');
  };

  const sameSite = (url) => url.includes('localhost:44475/') || url.includes('app.platformsolutions.com');

  // --- Content processing ---
  // The raw note/signature text
  const contentText = showSignature && signature ? `${note}\n\n${signature}` : note;
  // The processed content for Markdown rendering
  const processedContent = cleanMailTo(replaceLinks(contentText));

  const plainText = () => {
    try {
      const div = document.createElement("div");
      div.innerHTML = note;
      const children = div.querySelectorAll('*');
      for (let i = 0; i < children.length; i++) {
        if (children[i].textContent) children[i].textContent += ' ';
        else children[i].innerText += ' ';
      }
      return [div.textContent || div.innerText].toString().replace(/ +/g, ' ');
    } catch {
      return note;
    }
  }

  const MessageWrapper = ({ content }) => <Box
    sx={{
      minWidth: '100%',
      borderRadius: currentUser && !removeDetails ? '20px 20px 0 20px' : !removeDetails ? '20px 20px 20px 0' : '20px',
      wordWrap: 'break-word',
      typography: 'body2',
      p: 2,
      backgroundColor: () => `${alpha(stringToColor(sentBy), 0.05)}`,
      display: 'inline-block',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      '& img': {
        cursor: 'pointer',
        maxWidth: '100%',
        objectFit: 'contain',
        maxHeight: '200px',
        '&:hover': {
          filter: 'brightness(0.9)',
          padding: 0.3,
        },
      },
    }}
    onClick={(event) => {
      if (event.target.tagName === 'A') {
        event.preventDefault();
        if (sameSite(event.target.href)) {
          addNewTabAndNavigate(event.target.href);
        } else {
          window.open(event.target.href, '_blank');
        }
      }
      if (event.target.tagName === 'IMG') {
        onOpenLightbox(event.target.src);
      }
    }}
  >
    {content}
  </Box>;

  return (
    <Grid
      container
      direction="row"
      justifyContent={currentUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-end"
      sx={{
        mt: file ? 0 : removeDetails ? 0.5 : 2,
        p: 1,
        boxSizing: 'border-box',
        pr: currentUser ? 2 : 0
      }}
      key={index}
    >
      <Grid container direction="row" sx={{width: '100%', boxSizing: 'border-box'}} spacing={1}>
        {!currentUser && <Grid item sx={{mt: 'auto'}}>
          <CustomAvatar
            name={name || sentBy}
            email={sentBy}
            showTooltip={!removeDetails}
            sx={{opacity: removeDetails ? 0 : 1}}
          />
        </Grid>}
        <Grid item sx={{ flex: 1, minWidth: 0 }}>
          {file ? (
            <Grid container direction="row" sx={{ justifyContent: currentUser ? 'end' : 'start' }}>
              {fileArray && fileArray.length > 1 ? (
                fileArray.map((file, index) => (
                  <Grid
                    item
                    key={index}
                    width="100%"
                    sx={{
                      flexShrink: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      padding: '3px',
                      mx: 0.5,
                      my: 0.1,
                      width: 'fit-content',
                      border: `1px solid ${stringToColor(sentBy)}`,
                      backgroundColor: () => `${alpha(stringToColor(sentBy), 0.05)}`,
                    }}
                  >
                    <FileThumbnail
                      file={file.note}
                      tooltip
                      onDownload={() => getTicketFile(file.id)}
                      onImageView={() => onOpenLightbox(`/api/TicketFile?noteId=${file.id}`)}
                      preview={`/api/TicketFile?noteId=${file.id}`}
                    />
                    <Typography variant="caption" sx={{ color: (theme) => theme.palette.grey[700], ml: 1 }}>
                      {file.note}
                    </Typography>
                  </Grid>
                ))
              ) : (
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    ml: 0.75,
                    mr: 0.75,
                    display: 'flex',
                    padding: '3px',
                    margin: '1px',
                    width: 'fit-content',
                    border: `1px solid ${stringToColor(sentBy)}`,
                    backgroundColor: () => `${alpha(stringToColor(sentBy), 0.05)}`
                  }}
                >
                  <FileThumbnail
                    file={note}
                    tooltip
                    onDownload={() => getTicketFile(id)}
                    sx={{ width: 25, height: 25 }}
                    onImageView={() => onOpenLightbox(`/api/TicketFile?noteId=${id}`)}
                    preview={`/api/TicketFile?noteId=${id}`}
                  />
                  <Typography variant="caption" sx={{ color: (theme) => theme.palette.grey[700], ml: 1 }}>
                    {note}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : <>
              {!showAsPainText && <ErrorBoundary fallback={() =>
                <Alert severity="error" sx={{ width: '100%', py: 0, px: 1, m: 0, '.MuiAlert-message': { py: '6px' }, '.MuiSvgIcon-root': { height: '17px', width: '17px' } }}>
                  This message failed to display properly
                  <Typography disableRipple sx={{ ml: .5, color: "info.main", cursor: "pointer" }} variant="text" onClick={() => setShowAsPainText(true)}>
                    display as plain text
                  </Typography>
                </Alert>}>
                <Box sx={{ position: 'relative' }}>
                  {<MessageWrapper content={<Markdown children={processedContent} />} />}
                  {message.emailMessage && signature && (
                    <Tooltip title={showSignature ? 'Hide Signature' : 'Show Signature'}>
                      <IconButton
                        size="small"
                        onClick={() => setShowSignature(prev => !prev)}
                        sx={{
                          position: 'absolute',
                          bottom: '-12px',
                          left: '75%',
                          transform: 'translateX(-50%)',
                          p: 0,
                          height: '24px',
                          width: '24px',
                          backgroundColor: 'background.paper',
                          border: `1px solid ${stringToColor(sentBy)}`,
                          '&:hover': {
                            backgroundColor: 'background.paper',
                          }
                        }}
                      >
                        {showSignature ? <ArrowDropUpIcon sx={{ fontSize: '20px' }} /> : <ArrowDropDownIcon sx={{ fontSize: '20px' }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </ErrorBoundary>}
              {showAsPainText && <MessageWrapper content={<Box>
                {plainText()}
                <Box sx={{ mt: 2 }}>[Displaying message as plain text. Links, images and formatting won't show.]</Box>
              </Box>} />}
          </>}
          {removeDetails ? null : (
            <Grid
              direction="row"
              alignItems="center"
              container
              sx={{display: 'flex', justifyContent: currentUser ? 'right' : 'left'}}
            >
              <Typography noWrap variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }} component="span">
                {fullName} {fMonthDayTimeYear(message.sentOn)}
              </Typography>
              {message.emailMessage && (
                <Tooltip title="Message received via email">
                  <EmailIcon
                    onClick={downloadEmail}
                    sx={{ height: '14px', mb: -0.3, ml: 0.5, cursor: 'pointer' }}
                    color="secondary"
                  />
                </Tooltip>
              )}
            </Grid>
          )}
        </Grid>
        {currentUser && <Grid item sx={{ mt: 'auto' }}>
          <CustomAvatar
            name={name || sentBy}
            email={sentBy}
            showTooltip={!removeDetails}
            sx={{ opacity: removeDetails ? 0 : 1 }}
          />
        </Grid>}
      </Grid>
    </Grid>
  );
}
